/**
 * Case Research's topics: the research the case's type calls for, each with
 * how it runs and where it stands. Built from what the server already says:
 *
 * - the suggested questions (`/research/<id>/suggested-questions`), the
 *   legacy research areas filtered by the case's visa;
 * - the case's key facts (`/key-facts/<id>`), already filtered to its visa
 *   and posture: a topic a fact answers runs as that fact's lookup or
 *   analysis, and a notice case researches its notice's issues;
 * - the reputation pass (`/research/<id>/standing`), one per case;
 * - the research memos, matched by `research_topic`, else by title.
 *
 * With `?fit=1` each question also says whether the case calls for it: a
 * criterion topic follows the criteria Case Strategy argues, else whether its
 * evidence is in Case Files, and Casey guesses the rest. A topic the case
 * doesn't call for waits under More Topics.
 *
 * Pure, so the mapping and its fallbacks are testable.
 */
import type { Draft } from "@/api/types/draft";
import type { FsNode } from "@/features/caseFiles/types";
import { plainError, type PlainError } from "@/lib/plainError";
import { instanceLabel, pendingIsMissing } from "@/features/keyFacts/model";
import type {
  AnalysisState,
  KeyFact,
  KeyFactStatus,
  KeyFactsCase
} from "@/features/keyFacts/types";

import { draftStatus, isResearchMemo } from "../workspace/workspaceModel";

/** Who decided a topic's fit: the case files' categories, the criteria Case Strategy argues, or Casey. */
export type FitBy = "files" | "strategy" | "casey";

/** Whether the case's files call for a topic, and why. */
export type TopicFit = {
  applies: boolean;
  reason: string | null;
  by: FitBy;
};

/** A catalog entry, as `/suggested-questions` returns it. */
export type SuggestedQuestion = {
  id: string;
  topic: string;
  question: string;
  why: string;
  visa_types?: string[];
  legacy_id?: string | null;
  /** With `?fit=1`. */
  fit?: TopicFit | null;
};

export type StandingStatus = "none" | "running" | "done" | "failed";

/** The reputation pass; `off` when the environment doesn't have it (404). */
export type StandingState = { status: StandingStatus | "off" };

/**
 * How a topic runs: `notice` and `strategy` are a notice's issue analyses,
 * `lookup` a key fact's value, `analysis` a key fact's memo, `reputation`
 * the case's reputation pass, `memo` its own `start_research` memo, and
 * `read-notice` reads the notice before anything else can.
 */
export type ResearchRun =
  | "read-notice"
  | "notice"
  | "strategy"
  | "lookup"
  | "analysis"
  | "reputation"
  | "memo";

export type ResearchGroupId =
  | "notice"
  | "lookups"
  | "analyses"
  | "reputation"
  | "authority";

export const RESEARCH_GROUPS: readonly {
  id: ResearchGroupId;
  title: string;
}[] = [
  { id: "notice", title: "The Notice" },
  { id: "lookups", title: "Lookups" },
  { id: "analyses", title: "Analyses" },
  { id: "reputation", title: "Reputation" },
  { id: "authority", title: "Legal Authority" }
];

export type TopicStatus =
  | "not-started"
  | "running"
  | "done"
  | "stale"
  | "needs-you"
  | "failed";

export type ResearchTopic = {
  /** The catalog id; a notice issue's fact key; `read-notice`. */
  id: string;
  title: string;
  /** Why it matters for this case: the catalog's `why`. */
  why: string | null;
  /** The catalog question; what a memo topic researches. */
  question: string | null;
  run: ResearchRun;
  group: ResearchGroupId;
  /** The key fact a lookup, analysis or notice topic reads. */
  factKey: string | null;
  /** A lookup's value, shown inline. */
  value: string | null;
  status: TopicStatus;
  /** Whether the case's files call for it; `null` when nobody judged. */
  applies: boolean | null;
  /** Why it applies or doesn't ("No publications in Case Files"). */
  reason: string | null;
  /**
   * Why it's out of date, needs the attorney or failed, as a clause after
   * its title: "researched before Offer Letter.pdf was added". A failure
   * is said plainly, never cut.
   */
  issue: string | null;
  /** A failure's own wording, when `issue` had to say it plainly. */
  issueDetail?: string | null;
  /** Which of a topic's exhibits failed: " (4 exhibits)", " (CV.pdf)". */
  instances?: string;
};

export type ResearchPosture = "standard" | "notice" | "unread-notice";

export type ResearchTopics = {
  posture: ResearchPosture;
  /** The card's topics: the catalog's the case calls for, or a notice case's issues. */
  topics: ResearchTopic[];
  /** Under More Topics: the catalog's others, or a notice case's catalog. */
  more: ResearchTopic[];
  /** Who started the picks; `null` when the topics came without a fit. */
  pickedBy: FitBy | null;
};

/**
 * What the card has: topics, still loading, research not in the plan, or the
 * topics didn't load (the card reads its memos and runs the general prompt).
 */
export type ResearchTopicsState =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "unavailable" }
  | ({ kind: "ready" } & ResearchTopics);

// ---------------------------------------------------------------------------
// Title Case
// ---------------------------------------------------------------------------

const SMALL = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "by",
  "for",
  "in",
  "of",
  "on",
  "or",
  "the",
  "to",
  "vs",
  "with"
]);
/** Written as the agencies write them. */
const KEEP = new Map(
  [
    "STEM",
    "O*NET",
    "AAO",
    "USCIS",
    "USMCA",
    "CET",
    "CISA",
    "RFE",
    "NOID",
    "NOIR"
  ].map((w) => [w.toLowerCase(), w])
);

const capital = (word: string) =>
  word.replace(
    /^([("']?)(\p{L})/u,
    (_, lead: string, ch: string) => `${lead}${ch.toUpperCase()}`
  );

/** "AAO non-precedent decisions" → "AAO Non-Precedent Decisions". */
export const titleCase = (text: string): string =>
  text
    .trim()
    .split(/\s+/)
    .map((word, index) => {
      const kept = KEEP.get(word.toLowerCase());
      if (kept) return kept;
      if (/[A-Z].*[A-Z]/.test(word)) return word;
      if (index > 0 && SMALL.has(word.toLowerCase())) return word.toLowerCase();
      return word
        .split("-")
        .map((part) => KEEP.get(part.toLowerCase()) ?? capital(part))
        .join("-");
    })
    .join(" ");

// ---------------------------------------------------------------------------
// The mapping
// ---------------------------------------------------------------------------

type Mapping = { run: "lookup" | "analysis"; kind: string };

/** Catalog topics a key fact answers, by the fact's kind. */
export const FACT_TOPICS: Readonly<Record<string, Mapping>> = {
  area_of_expertise: { run: "analysis", kind: "field_of_expertise" },
  high_salary: { run: "analysis", kind: "high_remuneration" },
  specialty_occupation: { run: "analysis", kind: "specialty_occupation" },
  country_conditions: { run: "analysis", kind: "country_conditions" },
  about_the_petitioner: { run: "analysis", kind: "petitioner_profile" },
  onet_code: { run: "lookup", kind: "occupation" },
  wage_level: { run: "lookup", kind: "prevailing_wage" },
  journal_impact_factors: { run: "lookup", kind: "venue_rankings" },
  stem: { run: "lookup", kind: "stem" },
  cet: { run: "lookup", kind: "cet" },
  cisa: { run: "lookup", kind: "cisa" },
  bright_outlook_occupation: { run: "lookup", kind: "occupation_outlook" },
  usmca_profession: { run: "lookup", kind: "usmca_profession" }
};

/** Topics the case's one reputation pass covers (`start_research`'s standing topics). */
export const REPUTATION_TOPICS: ReadonlySet<string> = new Set([
  "award_reputation",
  "media_outlet_reputation",
  "association_reputation",
  "general_media_reputation",
  "distinguished_reputation_of_organizations",
  "judging"
]);

const AUTHORITY_TOPICS: ReadonlySet<string> = new Set([
  "uscis_policy_manual",
  "aao_precedent_decisions",
  "aao_non_precedent_decisions",
  "eb1a_standard",
  "o1a_standard",
  "eb1b_standard",
  "o1b_standard",
  "niw_merit_and_importance",
  "niw_well_positioned",
  "niw_balance"
]);

export const NOTICE_ISSUE_KIND = "notice_issue";
export const NOTICE_KIND = "notice";
export const NOTICE_STRATEGY_KEY = "notice_strategy";

const groupOf = (id: string): ResearchGroupId => {
  if (AUTHORITY_TOPICS.has(id)) return "authority";
  if (REPUTATION_TOPICS.has(id)) return "reputation";
  const mapped = FACT_TOPICS[id];
  if (mapped) return mapped.run === "lookup" ? "lookups" : "analyses";
  return "analyses";
};

// ---------------------------------------------------------------------------
// Statuses
// ---------------------------------------------------------------------------

const ANALYSIS_STATUS: Record<AnalysisState, TopicStatus> = {
  current: "done",
  outdated: "stale",
  running: "running",
  queued: "running",
  failed: "failed",
  cancelled: "not-started"
};

const FACT_STATUS: Record<KeyFactStatus, TopicStatus> = {
  checking: "running",
  fresh: "done",
  stale: "stale",
  needs_input: "needs-you",
  failed: "failed",
  disabled: "not-started"
};

/** A fact's analysis: its `analysis_state`; a fact still being read is running. */
export const analysisStatus = (fact: KeyFact): TopicStatus => {
  if (fact.analysis_state) return ANALYSIS_STATUS[fact.analysis_state];
  if (fact.status === "checking") return "running";
  if (fact.status === "needs_input") return "needs-you";
  if (fact.status === "failed") return "failed";
  return "not-started";
};

const missingInput = (missing: unknown): string => {
  if (typeof missing === "string") return missing.trim();
  if (!missing || typeof missing !== "object") return "";
  const extra = missing as Record<string, unknown>;
  return String(extra.input ?? extra.label ?? "").trim();
};

/**
 * Questions older journal-standing runs stored. A missing venue is not an
 * input: it does not block, and it is not something to ask the attorney.
 */
const VENUE_GAP = /^(the venue|publication venues?)$/i;

const isVenueGap = (text: string) => VENUE_GAP.test(text);

const journalUnknown = (fact: KeyFact): boolean =>
  fact.flags.some((f) => f.code === "journal_unknown") ||
  /^journal unknown$/i.test((fact.display ?? "").trim());

/**
 * Journal standing settled without a rank and without a question: a name
 * that is not a journal, an exhibit that names neither ("Journal unknown"),
 * or a stored needs-input whose only gap is the venue. A running, stale, or
 * failed lookup is left as it is.
 */
export const lookupFinishedWithoutRanking = (fact: KeyFact): boolean => {
  if (
    fact.status === "checking" ||
    fact.status === "stale" ||
    fact.status === "failed"
  )
    return false;
  if (fact.flags.some((f) => f.code === "venue_not_scholarly")) return true;
  if (/not a journal/i.test(fact.display ?? "")) return true;
  if (journalUnknown(fact)) return true;
  if (fact.status !== "needs_input") return false;
  const inputs = fact.missing.map((m) => missingInput(m)).filter(Boolean);
  return inputs.length > 0 && inputs.every(isVenueGap);
};

export const lookupStatus = (fact: KeyFact): TopicStatus => {
  if (lookupFinishedWithoutRanking(fact)) return "done";
  return FACT_STATUS[fact.status] ?? "not-started";
};

const STANDING_STATUS: Record<StandingStatus, TopicStatus> = {
  none: "not-started",
  running: "running",
  done: "done",
  failed: "failed"
};

/** `start_research`'s title for a question: "Research: " and its first 80 characters. */
export const memoTitleFor = (question: string): string => {
  const compact = question.split(/\s+/).filter(Boolean).join(" ");
  return `Research: ${compact.slice(0, 80)}${compact.length > 80 ? "…" : ""}`;
};

/** The case's research memos (not a fact's analysis), newest first. */
const memosOf = (drafts: Draft[]): Draft[] =>
  drafts
    .filter((d) => isResearchMemo(d) && !d.fact_key)
    .sort((a, b) =>
      (b.updated_at ?? b.created_at ?? "").localeCompare(
        a.updated_at ?? a.created_at ?? ""
      )
    );

/** The memo a topic wrote: by `research_topic`, else by the title `start_research` gives. */
export const memoFor = (
  memos: Draft[],
  topic: SuggestedQuestion
): Draft | null =>
  memos.find((d) => d.research_topic === topic.id) ??
  memos.find((d) => topic.legacy_id && d.research_topic === topic.legacy_id) ??
  memos.find(
    (d) => !d.research_topic && d.title?.trim() === memoTitleFor(topic.question)
  ) ??
  null;

export const memoStatus = (memo: Draft | null): TopicStatus => {
  if (!memo) return "not-started";
  switch (draftStatus(memo)) {
    case "running":
    case "waiting":
      return "running";
    case "failed":
      return "failed";
    case "ready":
      return "done";
    case "stale":
      return "stale";
    default:
      return "not-started";
  }
};

// ---------------------------------------------------------------------------
// Why a topic needs attention
// ---------------------------------------------------------------------------

/** "The worksite city and state" → "the worksite city and state". */
const lowerArticle = (text: string) =>
  text.replace(/^(The|A|An)\b/, (w) => w.toLowerCase());

export const upperFirst = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1);

const nodeName = (node: FsNode) => node.title ?? node.name;

/** Files added to Case Files after `since`. */
export const filesAddedSince = (
  nodes: readonly FsNode[],
  since: string | null | undefined
): FsNode[] => {
  const at = since ? Date.parse(since) : NaN;
  if (Number.isNaN(at)) return [];
  return nodes.filter(
    (n) =>
      n.kind === "file" &&
      !n.is_deleted &&
      !!n.created_at &&
      Date.parse(n.created_at) > at
  );
};

/** "Offer Letter.pdf was added", "3 files were added". */
const addedPhrase = (files: FsNode[]) =>
  files.length === 1
    ? `${nodeName(files[0])} was added`
    : `${files.length} files were added`;

/** An analysis written for an earlier value or evidence. */
const outdatedAnalysis = (fact: KeyFact, nodes: readonly FsNode[]): string => {
  const a = fact.analysis;
  if (a?.evidence_changed) {
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const cited = Object.entries(a.evidence ?? {}).map(([id, sum]) => ({
      node: byId.get(id),
      sum
    }));
    const removed = cited.filter((c) => !c.node || c.node.is_deleted);
    const changed = cited.filter(
      (c) =>
        c.node &&
        !c.node.is_deleted &&
        !!c.node.checksum &&
        c.node.checksum !== c.sum
    );
    const moved = removed.length + changed.length;
    if (moved > 1)
      return `${moved} files it cites changed since it was researched`;
    if (changed[0]?.node)
      return `${nodeName(changed[0].node)} changed since it was researched`;
    if (removed[0])
      return removed[0].node
        ? `${nodeName(removed[0].node)} was removed since it was researched`
        : "a file it cites was removed since it was researched";
    return "a file it cites changed since it was researched";
  }
  const added = filesAddedSince(nodes, a?.completed_at);
  if (added.length) return `researched before ${addedPhrase(added)}`;
  if (
    a?.fact_value_fingerprint &&
    fact.value_fingerprint &&
    a.fact_value_fingerprint !== fact.value_fingerprint
  )
    return fact.display?.trim()
      ? `researched before ${fact.label} changed to ${fact.display.trim()}`
      : `researched for an earlier ${fact.label}`;
  return "the case changed since it was researched";
};

/** A looked-up value the case record no longer gives. */
const staleLookup = (fact: KeyFact, nodes: readonly FsNode[]): string => {
  if (fact.pending) {
    if (pendingIsMissing(fact.pending))
      return fact.display?.trim()
        ? `the case record no longer supports ${fact.display.trim()}`
        : "the case record no longer supports its value";
    return `the case record now gives ${fact.pending.display}`;
  }
  const added = filesAddedSince(nodes, fact.updated_at);
  return added.length
    ? `looked up before ${addedPhrase(added)}`
    : "the case record changed since it was looked up";
};

/** What Casey is waiting on, from the fact's missing inputs. */
const waitingOn = (fact: KeyFact): string => {
  const inputs = fact.missing
    .map((m) => missingInput(m))
    .filter((text) => text && !isVenueGap(text));
  if (!inputs.length) return "Casey needs an answer in Case Insights";
  const more = inputs.length - 1;
  return `Casey is waiting on ${lowerArticle(inputs[0])}${
    more ? ` and ${more} more` : ""
  }`;
};

/** Why a fact's run failed, in plain words with the server's own beside it. */
export const factFailure = (
  fact: KeyFact,
  analysis: boolean
): PlainError | null =>
  plainError(analysis ? fact.analysis?.error ?? fact.error : fact.error);

/** Why a fact's topic is where it is, when that needs the attorney. */
export const factIssue = (
  fact: KeyFact,
  status: TopicStatus,
  analysis: boolean,
  nodes: readonly FsNode[]
): string | null => {
  switch (status) {
    case "stale":
      return analysis && fact.analysis_state === "outdated"
        ? outdatedAnalysis(fact, nodes)
        : staleLookup(fact, nodes);
    case "needs-you":
      return waitingOn(fact);
    case "failed":
      return factFailure(fact, analysis)?.text ?? null;
    default:
      return null;
  }
};

const memoIssue = (
  memo: Draft | null,
  status: TopicStatus,
  nodes: readonly FsNode[]
): string | null => {
  if (!memo) return null;
  if (status === "failed")
    return plainError(memo.agent_failure_reason)?.text ?? null;
  if (status !== "stale") return null;
  const added = filesAddedSince(nodes, memo.updated_at ?? memo.created_at);
  return added.length
    ? `researched before ${addedPhrase(added)}`
    : "the case changed since it was researched";
};

// ---------------------------------------------------------------------------
// Building the topics
// ---------------------------------------------------------------------------

export type ResearchInput = {
  questions: SuggestedQuestion[];
  facts: KeyFactsCase | null;
  standing: StandingState;
  drafts: Draft[];
  /** The fit summary's `by`, when the questions came with one. */
  fitBy?: FitBy | null;
  /** Case Files, to say what changed since a topic ran. */
  nodes?: readonly FsNode[];
};

/** The facts of a kind: one, or one per exhibit ("Venue Rankings: CV.pdf"). */
const factsByKind = (facts: KeyFact[], kind: string): KeyFact[] =>
  facts.filter((f) => f.kind === kind || f.key === kind);

/**
 * Which instance speaks for the topic. A running lookup leads. A real
 * ranking (`done`) wins over a leftover needs-you on another exhibit, so a
 * ranked journal is not wiped out by a lab name. A missing venue or
 * "Journal unknown" does not speak over a real question, and is not
 * needs-you when it is the only gap.
 */
const URGENCY: readonly TopicStatus[] = [
  "running",
  "done",
  "stale",
  "needs-you",
  "failed",
  "not-started"
];

type FactRun = { fact: KeyFact; status: TopicStatus; analysis: boolean };

const urgent = (runs: FactRun[]): FactRun =>
  [...runs].sort(
    (a, b) => URGENCY.indexOf(a.status) - URGENCY.indexOf(b.status)
  )[0];

/** A settled non-question (missing venue, unknown journal, not a journal). */
const quietSettlement = (run: FactRun): boolean =>
  !run.analysis &&
  run.status === "done" &&
  lookupFinishedWithoutRanking(run.fact);

/** One catalog topic, run by its fact, the reputation pass or a memo. */
const catalogTopic = (
  q: SuggestedQuestion,
  facts: KeyFact[],
  standing: StandingState,
  memos: Draft[],
  nodes: readonly FsNode[]
): ResearchTopic => {
  const base = {
    id: q.id,
    title: titleCase(q.topic),
    why: q.why?.trim() || null,
    question: q.question,
    group: groupOf(q.id),
    factKey: null,
    value: null,
    applies: q.fit ? q.fit.applies : null,
    reason: q.fit?.reason?.trim() || null,
    issue: null
  };
  const mapped = FACT_TOPICS[q.id];
  const found = mapped ? factsByKind(facts, mapped.kind) : [];
  if (mapped && found.length) {
    const runs = found.map((fact): FactRun => {
      const analysis = mapped.run === "analysis" && fact.has_analysis;
      return {
        fact,
        analysis,
        status: analysis ? analysisStatus(fact) : lookupStatus(fact)
      };
    });
    const voiced = runs.filter((run) => !quietSettlement(run));
    const top = urgent(voiced.length ? voiced : runs);
    const same = runs.filter((r) => r.status === top.status).length;
    const issue = factIssue(top.fact, top.status, top.analysis, nodes);
    const where =
      found.length < 2
        ? ""
        : same > 1
        ? ` (${same} exhibits)`
        : ` (${instanceLabel(top.fact)})`;
    return {
      ...base,
      run: top.analysis ? "analysis" : "lookup",
      factKey: top.fact.key,
      value:
        top.analysis || found.length > 1
          ? null
          : top.fact.display?.trim() || null,
      status: top.status,
      ...(top.status === "failed"
        ? {
            issue,
            issueDetail: factFailure(top.fact, top.analysis)?.detail ?? null,
            instances: where
          }
        : { issue: issue ? `${issue}${where}` : null })
    };
  }
  if (REPUTATION_TOPICS.has(q.id) && standing.status !== "off")
    return {
      ...base,
      run: "reputation",
      status: STANDING_STATUS[standing.status] ?? "not-started"
    };
  const memo = memoFor(memos, q);
  const status = memoStatus(memo);
  return {
    ...base,
    run: "memo",
    status,
    issue: memoIssue(memo, status, nodes),
    ...(status === "failed"
      ? { issueDetail: plainError(memo?.agent_failure_reason)?.detail ?? null }
      : {})
  };
};

type IssueValue = {
  title?: string;
  order?: number;
  procedural?: boolean;
  in_force?: boolean;
};

const issueValue = (fact: KeyFact): IssueValue =>
  fact.value && typeof fact.value === "object"
    ? (fact.value as IssueValue)
    : {};

/** A notice's issues to research, in notice order, without the procedural ones. */
const noticeIssues = (facts: KeyFact[]): KeyFact[] => {
  const issues = facts.filter(
    (f) => f.kind === NOTICE_ISSUE_KIND && !issueValue(f).procedural
  );
  const inForce = issues.filter((f) => issueValue(f).in_force !== false);
  return (inForce.length ? inForce : issues).sort(
    (a, b) =>
      (issueValue(a).order ?? Number.MAX_SAFE_INTEGER) -
        (issueValue(b).order ?? Number.MAX_SAFE_INTEGER) ||
      a.key.localeCompare(b.key)
  );
};

const issueTopic = (
  fact: KeyFact,
  index: number,
  nodes: readonly FsNode[]
): ResearchTopic => ({
  id: fact.key,
  title:
    issueValue(fact).title?.trim() ||
    fact.display?.trim() ||
    `Issue ${index + 1}`,
  why: null,
  question: null,
  run: "notice",
  group: "notice",
  factKey: fact.key,
  value: null,
  status: analysisStatus(fact),
  applies: null,
  reason: null,
  issue: factIssue(fact, analysisStatus(fact), true, nodes),
  issueDetail:
    analysisStatus(fact) === "failed"
      ? factFailure(fact, true)?.detail ?? null
      : null
});

export const READ_NOTICE_ID = "read-notice";

const READ_NOTICE: ResearchTopic = {
  id: READ_NOTICE_ID,
  title: "Read the Notice",
  why: "The notice's issues decide what to research.",
  question: null,
  run: "read-notice",
  group: "notice",
  factKey: null,
  value: null,
  status: "not-started",
  applies: null,
  reason: null,
  issue: null
};

const isNoticeWorkflow = (workflow: string | undefined) =>
  workflow === "rfe" || workflow === "noir_noid";

/** What the card counts: topics that apply, and any that ran anyway. */
const counted = (topic: ResearchTopic): boolean =>
  topic.applies !== false || topic.status !== "not-started";

export const buildResearchTopics = (input: ResearchInput): ResearchTopics => {
  const facts = input.facts?.facts ?? [];
  const memos = memosOf(input.drafts);
  const nodes = input.nodes ?? [];
  const catalog = input.questions.map((q) =>
    catalogTopic(q, facts, input.standing, memos, nodes)
  );
  const pickedBy = input.fitBy ?? null;
  if (!isNoticeWorkflow(input.facts?.workflow))
    return {
      posture: "standard",
      topics: catalog.filter(counted),
      more: catalog.filter((t) => !counted(t)),
      pickedBy
    };

  const read = facts.some((f) => f.kind === NOTICE_KIND);
  if (!read)
    return {
      posture: "unread-notice",
      topics: [READ_NOTICE],
      more: catalog,
      pickedBy
    };

  const issues = noticeIssues(facts).map((f, i) => issueTopic(f, i, nodes));
  const strategy = facts.find((f) => f.key === NOTICE_STRATEGY_KEY);
  const topics = strategy
    ? [
        ...issues,
        {
          id: NOTICE_STRATEGY_KEY,
          title: "Strategy Across Issues",
          why: "The issues taken together: eligibility paths, alternative theories and the argument order.",
          question: null,
          run: "strategy" as const,
          group: "notice" as const,
          factKey: strategy.key,
          value: null,
          status: analysisStatus(strategy),
          applies: null,
          reason: null,
          issue: factIssue(strategy, analysisStatus(strategy), true, nodes),
          issueDetail:
            analysisStatus(strategy) === "failed"
              ? factFailure(strategy, true)?.detail ?? null
              : null
        }
      ]
    : issues;
  return { posture: "notice", topics, more: catalog, pickedBy };
};

// ---------------------------------------------------------------------------
// Selection and the card's line
// ---------------------------------------------------------------------------

export type TopicPicks = Record<string, boolean>;

/** Card topics the case's files call for, not done; More Topics wait to be picked. */
export const defaultPicked = (topic: ResearchTopic, inMore: boolean): boolean =>
  !inMore && topic.status !== "done" && topic.applies !== false;

export const pickedTopics = (
  t: ResearchTopics,
  picks: TopicPicks = {}
): ResearchTopic[] => [
  ...t.topics.filter((x) => picks[x.id] ?? defaultPicked(x, false)),
  ...t.more.filter((x) => picks[x.id] ?? defaultPicked(x, true))
];

const count = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

const PLAIN: Partial<Record<TopicStatus, string>> = {
  stale: "out of date",
  "needs-you": "needs your answer in Case Insights",
  failed: "failed"
};

/**
 * "Journal Standing: researched before CV.pdf was added"; a failure as
 * "Journal Standing (4 exhibits) failed: why", its server wording on a
 * second line.
 */
export const topicLine = (topic: ResearchTopic): string => {
  if (topic.status !== "failed")
    return `${topic.title}: ${
      topic.issue ?? PLAIN[topic.status] ?? "out of date"
    }`;
  const head = `${topic.title}${topic.instances ?? ""} failed`;
  const line = topic.issue ? `${head}: ${topic.issue}` : head;
  return topic.issueDetail ? `${line}\n${topic.issueDetail}` : line;
};

const ATTENTION: readonly TopicStatus[] = ["failed", "stale", "needs-you"];

export type ResearchView = {
  status: "ready" | "running" | "done" | "stale" | "failed" | "needs-you";
  detail: string;
  /** What's behind the status, a topic a line, most pressing first. */
  reasons: string[];
  /** Only answers in Case Insights move it forward; nothing to run. */
  opens?: "insights";
};

/** The card's status, line and reasons, from its topics. */
export const researchView = (t: ResearchTopics): ResearchView => {
  if (t.posture === "unread-notice")
    return { status: "ready", detail: "Read the notice first", reasons: [] };
  const all = t.topics.filter(counted);
  if (!all.length)
    return {
      status: "ready",
      detail:
        t.topics.length || (t.posture === "standard" && t.more.length)
          ? "No topics the files call for"
          : "No topics for this case",
      reasons: []
    };
  const of = (s: TopicStatus) => all.filter((x) => x.status === s);
  const n = (s: TopicStatus) => of(s).length;
  const done = n("done");
  const noun = t.posture === "notice" ? "issue" : "topic";
  const reasons = ATTENTION.flatMap((s) => of(s).map(topicLine));
  if (n("running"))
    return {
      status: "running",
      detail:
        n("running") === 1
          ? `Researching ${of("running")[0].title}`
          : `Researching ${count(n("running"), noun)}`,
      reasons: []
    };
  if (n("failed"))
    return {
      status: "failed",
      detail: `${count(n("failed"), noun)} failed`,
      reasons
    };
  const needs = n("needs-you")
    ? `${count(n("needs-you"), noun)} ${
        n("needs-you") === 1 ? "needs" : "need"
      } you`
    : null;
  if (n("stale"))
    return {
      status: "stale",
      detail: [`${count(n("stale"), noun)} out of date`, needs]
        .filter(Boolean)
        .join(" · "),
      reasons
    };
  const line = `${done} of ${count(all.length, noun)} done`;
  if (needs) {
    const toRun = all.some(
      (x) => x.status === "not-started" && x.applies !== false
    );
    return toRun
      ? { status: "ready", detail: `${line} · ${needs}`, reasons }
      : { status: "needs-you", detail: needs, reasons, opens: "insights" };
  }
  return {
    status: done === all.length ? "done" : "ready",
    detail: line,
    reasons
  };
};

/**
 * The Run button's label for the picked topics: "Update Journal Standing",
 * "Run 4 Topics"; the status's own label when nothing is picked.
 */
export const researchAction = (
  picked: readonly ResearchTopic[],
  posture: ResearchPosture,
  fallback: string | null
): string | null => {
  if (!fallback || !picked.length) return fallback;
  const verb = picked.every((x) => x.status === "stale")
    ? "Update"
    : picked.every((x) => x.status === "failed")
    ? "Retry"
    : "Run";
  if (picked.length === 1) return `${verb} ${picked[0].title}`;
  return `${verb} ${picked.length} ${
    posture === "standard" ? "Topics" : "Issues"
  }`;
};
